import { useState } from 'react'
import { Icon } from '@iconify/react'
import { patch } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import Modal from '../../../../components/modal/Modal'
import FormLoader from '../../../../components/loader/FormLoader'
import '../kyc/KycDetail.scss'

const PRESET_REASONS = [
  'ID image is blurry or unreadable',
  'Image is not an ID document',
  'Wrong type of ID for what was declared',
  'ID number on the document does not match',
  'Name on the ID does not match',
  'ID document is expired',
  'Document appears altered or fraudulent',
]

interface Props {
  identityId: string
  shipperName: string
  // The version the reviewer is looking at — refused (409) if the business
  // has uploaded a newer one since.
  version: number
  decision: 'approved' | 'rejected'
  onClose: () => void
  onSuccess: () => void
}

// Approve or reject a shipper ID. A rejected ID can't be booked with until the
// business uploads a new image, which puts it back to pending. The business
// is not notified — they see the reason on their next booking attempt.
const ReviewShipperIdModal = ({
  identityId,
  shipperName,
  version,
  decision,
  onClose,
  onSuccess,
}: Props) => {
  const { notify } = useGlobalContext()
  const [selected, setSelected] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [loading, setLoading] = useState(false)

  const rejecting = decision === 'rejected'
  const reason = selected === 'other' ? customReason.trim() : selected

  const submit = () => {
    if (rejecting && reason.length < 5) {
      notify('error', 'Select or write a rejection reason')
      return
    }
    setLoading(true)
    patch(`admin/customer-identities/${identityId}/review`, {
      decision,
      version,
      ...(rejecting ? { reason } : {}),
    })
      .then((res: any) => {
        if (res.success) {
          notify('success', rejecting ? 'Shipper ID rejected' : 'Shipper ID approved')
          onClose()
        } else notify('error', res.message)
        // A 409 (newer version uploaded) also needs the page reloaded.
        onSuccess()
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  return (
    <Modal position='center'>
      <div className={`kyc-confirm-modal ${rejecting ? 'reject' : ''}`}>
        <div className='modal-header'>
          <div className={`modal-icon ${rejecting ? 'icon-red' : 'icon-green'}`}>
            <Icon icon={rejecting ? 'mdi:close-circle-outline' : 'mdi:shield-check'} />
          </div>
          <h2>{rejecting ? 'Reject Shipper ID' : 'Approve Shipper ID'}</h2>
          <p>
            {rejecting ? (
              <>
                Bookings for <b>{shipperName}</b> will be refused with this reason
                until the business uploads a new photo of the ID.
              </>
            ) : (
              <>
                You are confirming that version {version} of <b>{shipperName}</b>'s ID
                is genuine and matches the details the business gave.
              </>
            )}
          </p>
        </div>

        {rejecting && (
          <div className='reason-list'>
            {PRESET_REASONS.map((preset) => (
              <label
                key={preset}
                className={`reason-option ${selected === preset ? 'selected' : ''}`}
              >
                <input
                  type='radio'
                  name='reason'
                  checked={selected === preset}
                  onChange={() => setSelected(preset)}
                />
                <span>{preset}</span>
              </label>
            ))}
            <label className={`reason-option ${selected === 'other' ? 'selected' : ''}`}>
              <input
                type='radio'
                name='reason'
                checked={selected === 'other'}
                onChange={() => setSelected('other')}
              />
              <span>Other reason…</span>
            </label>
            {selected === 'other' && (
              <textarea
                rows={3}
                placeholder='Explain what the business needs to fix…'
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                autoFocus
              />
            )}
          </div>
        )}

        {loading ? (
          <FormLoader position='center' />
        ) : (
          <div className='modal-actions'>
            <button className='btn-cancel' onClick={onClose}>
              Cancel
            </button>
            {rejecting ? (
              <button className='btn-danger' onClick={submit} disabled={!reason}>
                Reject ID
              </button>
            ) : (
              <button className='btn-approve' onClick={submit}>
                Approve ID
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ReviewShipperIdModal
